<script>
  import { get } from 'vuex-pathify'
  import screen from '@/helpers/screen'
  import debounce from '@/helpers/debounce'
  import { DEFAULT_COLLECTION } from '@/helpers/marketCollections'
  import HeaderTicker from '@/components/marketplace/HeaderTicker'
  // COMMENTED OUT: old MarketHero — replaced by MarketCollectionHero
  // import MarketHero from '@/components/marketplace/MarketHero'
  import MarketCollectionHero from '@/components/marketplace/MarketCollectionHero'
  // COMMENTED OUT: MarketFeaturedTabs — not in Figma redesign
  // import MarketFeaturedTabs from '@/components/marketplace/MarketFeaturedTabs'
  import MarketCatalog from '@/components/marketplace/MarketCatalog'
  import MarketCategoryPills from '@/components/marketplace/MarketCategoryPills'
  import MarketModal from '@/components/marketplace/MarketModal'
  import MarketPromoModal from '@/components/marketplace/MarketPromoModal'
  import MarketPromoTab from '@/components/marketplace/MarketPromoTab'
  import marketPromoMixin from '@/mixins/marketPromoMixin'

  export default {
    metaInfo() {
      // Product detail view — rendered inside a portal, so we handle it here
      if (this.isPDPView) {
        const productId = this.route.params ? Number(this.route.params.productId) : null
        const product = productId ? (this.products || []).find((p) => p.id === productId) : null
        if (product) {
          const plainDescription = product.description
            ? product.description
              .replace(/<[^>]+>/g, '')
              .trim()
              .slice(0, 155)
            : `Shop ${product.title} in the Alltrue Add-On Market and save up to 70% off retail.`
          const images = product.images && product.images.length ? product.images : undefined
          const primaryImage = images ? images[0] : undefined
          const slug = this.route.params ? this.route.params.productSlug : undefined
          const productUrl = `https://shop.alltrue.com/app/market/product-list/products/${
            product.id
          }${slug ? `/${slug}` : ''}`
          const variant = product.defaultVariant
          const price = variant ? variant.price : undefined
          const sku = variant ? variant.sku : undefined
          const inStock = variant ? variant.hasUnlimitedStock || variant.stock > 0 : false
          const discountPct =
            product.retailValue && price && product.retailValue > price
              ? Math.round(((product.retailValue - price) / product.retailValue) * 100)
              : null
          const pdpTitle = discountPct
            ? `${product.title} — ${discountPct}% Off | Alltrue Market`
            : `${product.title} | Alltrue Add-On Market`
          return {
            title: pdpTitle,
            meta: [
              { vmid: 'description', name: 'description', content: plainDescription },
              { vmid: 'og:title', property: 'og:title', content: pdpTitle },
              { vmid: 'og:url', property: 'og:url', content: productUrl },
              { vmid: 'og:type', property: 'og:type', content: 'product' },
              ...(primaryImage
                ? [{ vmid: 'og:image', property: 'og:image', content: primaryImage }]
                : []),
            ],
            link: [{ rel: 'canonical', href: productUrl }],
            script: [
              {
                vmid: 'json-ld',
                type: 'application/ld+json',
                json: {
                  '@context': 'https://schema.org',
                  '@type': 'Product',
                  name: product.title,
                  description: plainDescription,
                  url: productUrl,
                  ...(images ? { image: images } : {}),
                  ...(sku ? { sku } : {}),
                  ...(product.brand ? { brand: { '@type': 'Brand', name: product.brand } } : {}),
                  offers: {
                    '@type': 'Offer',
                    priceCurrency: 'USD',
                    ...(price !== undefined ? { price: price.toFixed(2) } : {}),
                    availability: inStock
                      ? 'https://schema.org/InStock'
                      : 'https://schema.org/OutOfStock',
                    itemCondition: 'https://schema.org/NewCondition',
                    url: productUrl,
                    seller: { '@type': 'Organization', name: 'Alltrue' },
                  },
                },
              },
            ],
          }
        }
      }

      // Collection / product list view
      const { seoTitle, description, canonicalUrl, itemListElements } = this.seoMeta
      return {
        title: seoTitle,
        meta: [
          { vmid: 'description', name: 'description', content: description },
          { vmid: 'og:title', property: 'og:title', content: seoTitle },
          { vmid: 'og:url', property: 'og:url', content: canonicalUrl },
          { vmid: 'og:type', property: 'og:type', content: 'website' },
        ],
        link: [{ rel: 'canonical', href: canonicalUrl }],
        script: [
          {
            vmid: 'json-ld',
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: seoTitle,
              description,
              url: canonicalUrl,
              mainEntity: {
                '@type': 'ItemList',
                itemListElement: itemListElements,
              },
            },
          },
        ],
      }
    },
    components: {
      HeaderTicker,
      // COMMENTED OUT: old MarketHero
      // MarketHero,
      MarketCollectionHero,
      // COMMENTED OUT: MarketFeaturedTabs
      // MarketFeaturedTabs,
      MarketCatalog,
      MarketCategoryPills,
      MarketModal,
      MarketPromoModal,
      MarketPromoTab,
    },
    mixins: [marketPromoMixin],
    data() {
      return {
        heroAboveViewport: false,
      }
    },
    computed: {
      ...get(['route']),
      ...get('config', ['currentSeason', 'marketplaceEnabled']),
      ...get('feature', ['flags']),
      ...get('account', ['isAdmin']),
      ...get('account', { accountLoaded: 'loaded' }),
      ...get('marketplace', ['topTenProducts', 'isPDPView', 'filteredProducts', 'products', 'collectionSlug']),
      ...get('marketplaceCart', ['isCartEmpty']),
      ...get('prismic', ['marketCollectionPillsData']),
      screen,
      activeCollection() {
        const slug = (this.route.params ? this.route.params.collection : null) ?? this.collectionSlug
        if (!slug) return DEFAULT_COLLECTION
        if (this.marketCollectionPillsData?.length) {
          const prismicItem = this.marketCollectionPillsData.find((i) => i.collection_slug === slug)
          if (prismicItem) {
            return {
              slug,
              title: prismicItem.collection_title,
              breadcrumb: prismicItem.collection_title,
              heroColor: `#${prismicItem.color_hex}`,
              heroTextColor: prismicItem.dark_text ? '#002823' : '#F5EBD6',
              filters: {},
            }
          }
        }
        return { ...DEFAULT_COLLECTION, slug }
      },
      marketClosed() {
        if (!this.accountLoaded) return false
        return !this.marketplaceEnabled && !this.isAdmin
      },
      seoMeta() {
        const isCollection = !!this.activeCollection?.slug
        const collectionTitle = this.activeCollection?.title || 'All Products'
        const pageTitle = isCollection ? collectionTitle : 'All Products'
        const seoTitle = isCollection
          ? `Shop ${collectionTitle} — Up to 70% Off | Alltrue Market`
          : 'Shop All Products — Up to 70% Off | Alltrue Add-On Market'
        const description = isCollection
          ? `Shop ${collectionTitle} in the Alltrue Add-On Market — deeply discounted products at up to 70% off retail. No membership required.`
          : 'Save up to 70% off retail in the Alltrue Add-On Market. Hundreds of curated products across home goods, beauty, wellness, pets, and more.'
        const canonicalPath = isCollection
          ? `/app/market/product-list/${this.activeCollection.slug}`
          : '/app/market/product-list'
        const canonicalUrl = `https://shop.alltrue.com${canonicalPath}`
        const itemListElements = (this.filteredProducts || []).slice(0, 10).map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: p.title,
          url: `https://shop.alltrue.com/app/market/product-list/products/${p.id}`,
        }))
        return { pageTitle, seoTitle, description, canonicalUrl, itemListElements }
      },
    },
    async created() {
      this.$store.dispatch('marketplace/ensureFresh')
      this.$store.dispatch('products/ensureFresh')
      this.$store.dispatch('prismic/loadMarketCollectionPillsData')
      // Watch for changes to route filters
      const unwatchRoute = this.$store.watch(
        () => this.$store.get('route'),
        async () => {
          const route = this.$store.get('route')
          if (route.name === 'MarketProductList' || route.name === 'MarketCollection') {
            // Save the active collection slug so the catalog stays stable when the
            // product detail modal opens and strips the collection from route params.
            this.$store.set('marketplace/collectionSlug', route.params?.collection || null)
            // Collection preset filters are handled by the collectionProducts store getter.
            // Here we only restore user-applied filters from query params (or clear them).
            const filtersFromQuery = { tags: [], categories: [], brands: [] }
            if (route.query) {
              if (route.query.tags) filtersFromQuery.tags = route.query.tags.split(',')
              if (route.query.categories)
                filtersFromQuery.categories = route.query.categories.split(',')
              if (route.query.brands) filtersFromQuery.brands = route.query.brands.split(',')
            }
            this.$store.set('marketplace/filters', filtersFromQuery)
          }
        },
        { immediate: true }
      )
      this.$once('hook:beforeDestroy', () => {
        unwatchRoute()
      })
    },
    mounted() {
      // Refresh the `heroAboveViewport` property
      const ref = this.$refs.heroRef
      const heroElement = ref?.$el ?? ref
      const headerOffset = this.$twToPx(17, true)
      const refreshHeroAboveViewport = () => {
        this.heroAboveViewport = heroElement.getBoundingClientRect().bottom < headerOffset
      }
      if (heroElement) {
        refreshHeroAboveViewport()
        // Also refresh it as the user scrolls, with an IntersectionObserver
        const observer = new window.IntersectionObserver(() => refreshHeroAboveViewport(), {
          // Offset the header's height
          rootMargin: `-${headerOffset}px 0px 0px 0px`,
        })
        observer.observe(heroElement)
        this.$once('hook:beforeDestroy', () => {
          observer.disconnect()
        })
        // Also refresh it when the window is resized
        const handleResize = debounce(refreshHeroAboveViewport, 200)
        window.addEventListener('resize', handleResize)
        this.$once('hook:beforeDestroy', () => {
          window.removeEventListener('resize', handleResize)
        })
      }
    },
  }
</script>

<template>
  <div>
    <Portal to="headerLogo">
      <div
        v-if="heroAboveViewport && !screen.sm && !isPDPView && !isCartEmpty"
        class="flex items-center flex-1"
      >
        <HeaderTicker side="left" />
      </div>
    </Portal>

    <Portal to="headerNav">
      <div v-if="heroAboveViewport && screen.sm" class="flex items-center flex-1">
        <HeaderTicker side="right" />
      </div>
    </Portal>

    <!-- COMMENTED OUT: old MarketHero — replaced by MarketCollectionHero -->
    <!-- <MarketHero v-ref="heroRef" data-cy="market-hero" /> -->
    <MarketCollectionHero ref="heroRef" :collection="activeCollection" data-cy="market-hero" />
    <!-- COMMENTED OUT: MarketFeaturedTabs — not in Figma redesign -->
    <!--
    <MarketFeaturedTabs
      v-if="topTenProducts && topTenProducts.length > 0"
      class="overflow-hidden"
    />
    -->

    <!-- Category pills bar: sits between hero and catalog with its own background -->
    <div class="bg-dawn w-full">
      <BaseContainer max-width="2xl">
        <MarketCategoryPills />
      </BaseContainer>
    </div>

    <MarketCatalog />

    <Portal to="productModal">
      <RouterView />
    </Portal>

    <Portal v-if="marketClosed" to="modal">
      <MarketModal>
        <p>
          The Add-On Market is now closed! Please stay tuned to your Account page for shipping
          updates!
        </p>
      </MarketModal>
    </Portal>

    <!-- Post-customization promo modal (recurring subscribers only) -->
    <MarketPromoModal
      v-if="showPromoModal"
      :open="showPromoModal"
      :headline="promoHeadline"
      :subhead="promoSubhead"
      :cta-text="promoCtaText"
      :cta-url="promoUrl"
      :dismiss-text="promoDismissText"
      @dismiss="handlePromoDismiss"
    />

    <!-- Persistent promo tab — visible to eligible recurring subscribers on market pages -->
    <MarketPromoTab v-if="isPromoEligible" :label="promoTabLabel" @click="openPromo" />
  </div>
</template>
